import { useEffect, useState, useRef } from "react";
import ReactDOM from "react-dom/client";
import { db } from "../firebaseConfig";
import { doc, updateDoc, arrayUnion, arrayRemove } from "firebase/firestore";

import { useNative, useBackable } from "../NativeContext";
import { useAuth } from "../AuthContext";
import { useKiosk } from "../KioskContext";
import SideScreen from "../components/SideScreen";
import { ListButton, TextButton } from "../components/Buttons";

import style from "../style/screens/Location.module.css";

interface KioskData {
    id: string;
    docId: string;
    status: "PENDING" | "ACTIVE" | "INACTIVE" | "FULL";
    name: string;
    address: string;
    lat: number;
    lng: number;
    maxCapacity: number;
    currentCapacity: number;
    message: string;
    photo: string;
    timestamp: number;
}

class CustomOverlay extends window.naver.maps.OverlayView {
    private _element: HTMLDivElement;
    private _position: any;

    constructor(options: { map?: any; position: any; Marker: HTMLDivElement }) {
        super();
        this._element = options.Marker;
        this.setPosition(options.position);
        this.setMap(options.map || null);
    }

    setPosition(position: any) {
        this._position = position;
        this.draw();
    }

    getPosition() {
        return this._position;
    }

    onAdd() {
        const overlayLayer = this.getPanes().overlayLayer;
        overlayLayer.appendChild(this._element);
    }

    draw() {
        if (!this.getMap()) return;

        const projection = this.getProjection();
        const position = this.getPosition();
        const pixelPosition = projection.fromCoordToOffset(position);

        this._element.style.left = `${pixelPosition.x}px`;
        this._element.style.top = `${pixelPosition.y}px`;
    }

    onRemove() {
        this._element.remove();
    }
}

const Location = () => {
    const { isNative, location, postLocation } = useNative();
    const { kiosks, favorites } = useKiosk();

    const [searching, setSearching] = useState<boolean>(false);
    const [searchQuery, setSearchQuery] = useState<string>("");
    const [searchResult, setSearchResult] = useState<KioskData[]>([]);
    const [selectedKiosk, setSelectedKiosk] = useState<string>("");

    const [detail, setDetail] = useState<boolean>(false);
    const [myLocationClicked, setMyLocationClicked] = useState<boolean>(false);

    const mapRef = useRef<HTMLDivElement>(null);
    const mapInstance = useRef<any>(null);
    const [lat, setLat] = useState<number>(37.5642135);
    const [lng, setLng] = useState<number>(127.0016985);
    const [hasUserLocation, setHasUserLocation] = useState<boolean>(false);

    const myLocationMarkerRef = useRef<CustomOverlay>(null);
    const markerRefs = useRef<CustomOverlay[]>([]);

    useBackable("search", () => setSearching(false), searching);

    useEffect(() => {
        setDetail(Boolean(selectedKiosk));
    }, [selectedKiosk]);

    useEffect(() => {
        if (isNative && location) {
            setLat(location.coords.latitude);
            setLng(location.coords.longitude);
            setHasUserLocation(true);
        } else if (isNative) {
            setHasUserLocation(false);
        }
    }, [isNative, location]);

    useEffect(() => {
        renderMap();
    }, [lat, lng, hasUserLocation]);

    useEffect(() => {
        renderMarkers();
    }, [kiosks]);

    const renderMap = () => {
        if (!window.naver || !mapRef.current) return;

        let options = {
            center: 0,
            zoom: 15,
        };
        if (lat && lng) {
            options.center = new window.naver.maps.LatLng(lat, lng);
        } else {
            options.center = new window.naver.maps.LatLng(33.450701, 126.570667);
        }

        mapInstance.current = new window.naver.maps.Map(mapRef.current, options);
        mapInstance.current.setOptions({
            location: {
                enableHighAccuracy: true,
            },
        });

        if (hasUserLocation) {
            renderMyLocationMarker();
        } else if (myLocationMarkerRef.current) {
            myLocationMarkerRef.current.setMap(null);
        }
        if (markerRefs.current.length > 0) renderMarkers();

        window.naver.maps.Event.addListener(mapInstance.current, "zoom_changed", () => {
            renderMarkers();
        });
    };

    const renderMyLocationMarker = () => {
        if (!window.naver || !mapInstance.current || !lat || !lng) return;

        const myLatLng = new window.naver.maps.LatLng(lat, lng);
        const content = document.createElement("div");
        content.style.display = "block";
        content.style.position = "absolute";
        content.style.height = "12px";
        content.style.width = "12px";
        content.style.backgroundColor = "var(--color-brand)";
        content.style.border = "solid 3px white";
        content.style.borderRadius = "50%";
        content.style.boxShadow = "0px 0px 0px 10px rgba(69, 167, 234, 0.2)";

        if (myLocationMarkerRef.current) myLocationMarkerRef.current.setMap(null);

        myLocationMarkerRef.current = new window.naver.maps.Marker({
            position: myLatLng,
            map: mapInstance.current,
            icon: {
                content: content,
                anchor: new window.naver.maps.Point(8, 8),
            },
        });
    };

    const renderMarkers = () => {
        if (!window.naver || !mapInstance.current) return;

        // Clear Markers
        markerRefs.current.forEach((marker) => marker.setMap(null));
        markerRefs.current = [];

        const map = mapInstance.current;
        const zoom = map.getZoom();
        const baseDelta = 0.05;
        const delta = baseDelta / Math.pow(2, zoom - 10);
        // console.log(`Zoom Level: ${zoom}, Delta: ${delta}`);

        const coordinateMap = new Map<string, number>();
        kiosks.forEach((kiosk) => {
            const key = `${kiosk.lat},${kiosk.lng}`;
            coordinateMap.set(key, (coordinateMap.get(key) || 0) + 1);
        });

        const positionCountMap = new Map<string, number>();

        kiosks.forEach((kiosk) => {
            const key = `${kiosk.lat},${kiosk.lng}`;
            const index = positionCountMap.get(key) || 0;
            const total = coordinateMap.get(key) || 1;

            const angle = (index / total) * 2 * Math.PI;
            const randomOffset = (index / total) * ((Math.random() % 10) * delta);
            const offsetLat = delta * Math.sin(angle) + randomOffset;
            const offsetLng = delta * Math.cos(angle) + randomOffset;

            const position = new window.naver.maps.LatLng(kiosk.lat + offsetLat, kiosk.lng + offsetLng);
            // const position = new window.naver.maps.LatLng(kiosk.lat, kiosk.lng);

            const content = document.createElement("div");
            content.style.display = "block";
            content.style.position = "absolute";
            content.style.width = "max-content";
            const tempRoot = ReactDOM.createRoot(content);
            tempRoot.render(
                <Marker click={() => setSelectedKiosk(kiosk.docId)} available={kiosk.status === "ACTIVE" || false} />
            );

            const overlay = new CustomOverlay({
                map: map,
                position,
                Marker: content,
            });

            markerRefs.current.push(overlay);
            positionCountMap.set(key, index + 1);
        });

        // kiosks.forEach((kiosk) => {
        //     const position = new window.naver.maps.LatLng(kiosk.lat, kiosk.lng);

        //     const content = document.createElement("div");
        //     content.style.display = "block";
        //     content.style.position = "absolute";
        //     content.style.width = "max-content";
        //     const tempRoot = ReactDOM.createRoot(content);
        //     tempRoot.render(
        //         <Marker click={() => setSelectedKiosk(kiosk.docId)} available={kiosk.status == "ACTIVE" || false} />
        //     );

        //     new CustomOverlay({
        //         map: mapInstance.current,
        //         position,
        //         Marker: content,
        //     });
        // });
    };

    const centerMap = (latitude: number, longitude: number) => {
        if (!window.naver || !mapInstance.current) return;

        const myPosition = new window.naver.maps.LatLng(latitude, longitude);
        mapInstance.current.setCenter(myPosition);
        mapInstance.current.setZoom(15);
    };

    const moveToMyLocation = () => {
        if (isNative) {
            if (hasUserLocation) centerMap(lat, lng);
            else postLocation();
            return;
        }

        if (!navigator.geolocation) {
            console.error("Geolocation API를 지원하지 않는 브라우저입니다.");
            return;
        }

        navigator.geolocation.getCurrentPosition(
            (position) => {
                const latitude = position.coords.latitude;
                const longitude = position.coords.longitude;
                setLat(latitude);
                setLng(longitude);
                setHasUserLocation(true);
                centerMap(latitude, longitude);
            },
            (err) => {
                console.error(err);
                setHasUserLocation(false);
            }
        );
    };

    return (
        <>
            <div ref={mapRef} className={style["container--map"]} id="map"></div>

            {/* 내 위치로 돌아가기 버튼 */}
            <button
                className={[
                    style["button--my-location"],
                    myLocationClicked && style["button--my-location--clicked"],
                ].join(" ")}
                onMouseDown={() => setMyLocationClicked(true)}
                onMouseUp={() => setMyLocationClicked(false)}
                onTouchStart={() => setMyLocationClicked(true)}
                onTouchEnd={() => setMyLocationClicked(false)}
                onClick={moveToMyLocation}
                title="내 위치로 이동"
            >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                        d="M12 8C9.79 8 8 9.79 8 12C8 14.21 9.79 16 12 16C14.21 16 16 14.21 16 12C16 9.79 14.21 8 12 8ZM20.94 11C20.48 6.83 17.17 3.52 13 3.06V1H11V3.06C6.83 3.52 3.52 6.83 3.06 11H1V13H3.06C3.52 17.17 6.83 20.48 11 20.94V23H13V20.94C17.17 20.48 20.48 17.17 20.94 13H23V11H20.94ZM12 19C8.13 19 5 15.87 5 12C5 8.13 8.13 5 12 5C15.87 5 19 8.13 19 12C19 15.87 15.87 19 12 19Z"
                        fill="currentColor"
                    />
                </svg>
            </button>

            <div className={[style["container--kiosks"], searching && style["container--kiosks--active"]].join(" ")}>
                <div className={style["wrapper--search"]}>
                    <h4 className={[style["text--title"], searching && style["text--title--disable"]].join(" ")}>
                        즐겨찾기
                    </h4>

                    <Search
                        searching={searching}
                        searchQuery={searchQuery}
                        setSearchQuery={(query) => setSearchQuery(query)}
                        setSearchResult={(results) => setSearchResult(results)}
                        search={() => setSearching(true)}
                        cancel={() => {
                            setSearchQuery("");
                            setSearching(false);
                        }}
                    />
                </div>
                <div className={style["wrapper--scroll"]}>
                    <div className={style["wrapper--lists"]}>
                        <p
                            className={[
                                style["text--alert"],
                                !searching && favorites.length == 0 && style["text--alert--active"], // 즐겨찾기 표시 중
                            ].join(" ")}
                        >
                            {"즐겨찾기된 키오스크가 존재하지 않습니다"}
                        </p>

                        {searching ? (
                            searchQuery && searchResult.length == 0 ? (
                                <p className={[style["text--alert"], style["text--alert--active"]].join(" ")}>
                                    검색 결과가 존재하지 않습니다
                                </p>
                            ) : (
                                searchResult.map((kiosk, index) => (
                                    <List
                                        key={index}
                                        kioskId={kiosk.docId}
                                        name={kiosk.name}
                                        available={kiosk.status == "ACTIVE"}
                                        setKiosk={() => setSelectedKiosk(kiosk.docId)}
                                    />
                                ))
                            )
                        ) : (
                            favorites.length > 0 &&
                            favorites.map((fav, index) => {
                                let kiosk = kiosks!.filter((d) => d.docId == fav)[0];
                                if (!kiosk) return;

                                return (
                                    <List
                                        key={index}
                                        kioskId={kiosk.docId}
                                        name={kiosk.name}
                                        available={kiosk.status == "ACTIVE"}
                                        setKiosk={() => setSelectedKiosk(kiosk.docId)}
                                    />
                                );
                            })
                        )}
                    </div>
                </div>
            </div>

            <SideScreen
                header={false}
                appear={detail}
                // 뒤로가기 모듈을 위한 함수
                close={() => {
                    setDetail(false);
                    setTimeout(() => {
                        setSelectedKiosk("");
                    }, 400);
                }}
                id="L1"
            >
                {selectedKiosk && (
                    <Detail
                        selectedKioskID={selectedKiosk}
                        close={() => {
                            setDetail(false);
                            setTimeout(() => {
                                setSelectedKiosk("");
                            }, 400);
                        }}
                    />
                )}
            </SideScreen>
        </>
    );
};

const Search = ({
    searching,
    searchQuery,
    setSearchQuery,
    setSearchResult,
    search,
    cancel,
}: {
    searching: boolean;
    searchQuery: string;
    setSearchQuery: (query: string) => void;
    setSearchResult: (results: KioskData[]) => void;
    search: () => void;
    cancel: () => void;
}) => {
    const { kiosks } = useKiosk();

    const [btnClick, setBtnClick] = useState<boolean>(false);
    const searchInput = useRef<HTMLInputElement | null>(null);

    useEffect(() => {
        if (!searching) {
            setSearchQuery("");
        }
    }, [searching]);

    useEffect(() => {
        if (!searchQuery) {
            setSearchResult([]);
            return;
        }
        // 검색어를 공백 제거 후 비교하여 kiosks를 필터링
        const filtered: KioskData[] = kiosks!.filter((kiosk) => {
            const nameMatch = kiosk.name.toLowerCase().includes(searchQuery.trim().toLowerCase());
            const addressMatch = kiosk.address.toLowerCase().includes(searchQuery.trim().toLowerCase());
            return nameMatch || addressMatch;
        });
        setSearchResult(filtered);
    }, [searchQuery, kiosks]);

    return (
        <div className={[style["unit--search"], searching && style["unit--search--active"]].join(" ")}>
            <div className={style["wrapper--search-actions"]}>
                <button
                    // click events
                    onMouseDown={() => setBtnClick(true)}
                    onMouseUp={() => setBtnClick(false)}
                    onTouchStart={() => setBtnClick(true)}
                    onTouchEnd={() => setBtnClick(false)}
                    onClick={() => {
                        search();
                        // searchInput.current!.focus();
                    }}
                    className={[style["button--search"], btnClick && style["button--search--clicked"]].join(" ")}
                >
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="#46aaed" xmlns="http://www.w3.org/2000/svg">
                        <path d="M0.246094 8.11328C0.246094 3.81641 3.74219 0.320312 8.03906 0.320312C12.3359 0.320312 15.832 3.81641 15.832 8.11328C15.832 9.89062 15.2363 11.5215 14.2305 12.8301L19.0059 17.6348C19.2109 17.8398 19.3281 18.123 19.3281 18.4355C19.3281 19.0898 18.8691 19.5879 18.2051 19.5879C17.8926 19.5879 17.5996 19.4805 17.375 19.2559L12.5703 14.4414C11.291 15.3594 9.73828 15.9062 8.03906 15.9062C3.74219 15.9062 0.246094 12.4102 0.246094 8.11328ZM1.91602 8.11328C1.91602 11.4922 4.66016 14.2363 8.03906 14.2363C11.418 14.2363 14.1621 11.4922 14.1621 8.11328C14.1621 4.73438 11.418 1.99023 8.03906 1.99023C4.66016 1.99023 1.91602 4.73438 1.91602 8.11328Z" />
                    </svg>
                </button>

                <input
                    ref={searchInput}
                    className={style["input--search"]}
                    type="text"
                    placeholder="검색하기"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                />

                <TextButton text="취소하기" onClick={cancel} />
            </div>
        </div>
    );
};

const List = ({
    kioskId,
    name,
    available,
    setKiosk,
}: {
    kioskId: string;
    name: string;
    available: boolean;
    setKiosk: (id: string) => void;
}) => {
    return (
        <div className={style["unit--list"]}>
            <h4 className={style["text--list"]}>
                {name}
                {available ? (
                    <span className={style["text--list--available"]}>페트 투입 가능</span>
                ) : (
                    <span className={style["text--list--unavailable"]}>페트 꽉참</span>
                )}
            </h4>
            <ListButton
                type="Default"
                text="상세보기"
                onClick={() => {
                    setKiosk(kioskId);
                }}
            />
        </div>
    );
};

const Detail = ({ selectedKioskID, close }: { selectedKioskID: string; close: () => void }) => {
    const { user } = useAuth();
    const { kiosks, favorites } = useKiosk();

    const selectedKioskData = kiosks?.find((kiosk) => kiosk.docId === selectedKioskID) || null;

    const name = selectedKioskData!.name || "";
    const photo = selectedKioskData!.photo || "";
    const address = selectedKioskData!.address || "";
    const available = selectedKioskData!.status == "ACTIVE" || false;
    const currentCapacity = selectedKioskData!.currentCapacity || 0;
    const maxCapacity = selectedKioskData!.maxCapacity || 0;
    const message = selectedKioskData!.message || "용량이 꽉 찼어요! 금방 비워두겠습니다";

    const [clicked, setClicked] = useState<boolean>(false);
    const [saved, setSaved] = useState<boolean>(favorites.includes(selectedKioskData?.docId as string));
    const [disabled, setDisabled] = useState<boolean>(false);

    const save = async () => {
        if (disabled) return;

        let query = saved
            ? updateDoc(doc(db, "member", user!.uid), {
                  savedKiosk: arrayRemove(selectedKioskData?.docId),
              })
            : updateDoc(doc(db, "member", user!.uid), {
                  savedKiosk: arrayUnion(selectedKioskData?.docId),
              });

        setDisabled(true);
        setSaved(!saved);

        return query
            .then(() => {
                setDisabled(false);
            })
            .catch((err) => {
                console.error(err);
                setDisabled(false);
                return 0;
            });
    };

    return (
        <div className={style["container--kiosk"]}>
            <div className={style["unit--kiosk-photo"]} style={{ backgroundImage: `url(${photo})` }}>
                <button
                    onClick={close}
                    // click events
                    onMouseDown={() => setClicked(true)}
                    onMouseUp={() => setClicked(false)}
                    onTouchStart={() => setClicked(true)}
                    onTouchEnd={() => setClicked(false)}
                    className={[style["button--close"], clicked && style["button--close--clicked"]].join(" ")}
                >
                    <svg width="18" height="14" viewBox="0 0 18 14" fill="#1d1d1d;" xmlns="http://www.w3.org/2000/svg">
                        <path d="M0.834961 6.6543C0.834961 6.43457 0.931641 6.23242 1.10742 6.06543L6.92578 0.255859C7.11914 0.0625 7.30371 -0.0078125 7.51465 -0.0078125C7.94531 -0.0078125 8.28809 0.308594 8.28809 0.748047C8.28809 0.958984 8.20898 1.16113 8.06836 1.30176L6.09961 3.30566L3.14648 6.00391L5.26465 5.87207H16.3828C16.8398 5.87207 17.1562 6.19727 17.1562 6.6543C17.1562 7.11133 16.8398 7.43652 16.3828 7.43652H5.26465L3.1377 7.30469L6.09961 10.0029L8.06836 12.0068C8.20898 12.1387 8.28809 12.3496 8.28809 12.5605C8.28809 13 7.94531 13.3164 7.51465 13.3164C7.30371 13.3164 7.11914 13.2373 6.94336 13.0703L1.10742 7.24316C0.931641 7.07617 0.834961 6.87402 0.834961 6.6543Z" />
                    </svg>
                </button>
            </div>
            <div className={style["unit--kiosk-detail"]}>
                <div className={style["wrapper--kiosk-title"]}>
                    <h4 className={style["text--kiosk-name"]}>{name}</h4>
                    <button
                        onClick={save}
                        className={[style["button--save"], saved && style["button--save--saved"]].join(" ")}
                    >
                        <svg
                            width="16"
                            height="15"
                            viewBox="0 0 16 15"
                            fill="#ffffff"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path d="M2.91277 14.3828C2.63249 14.1709 2.57097 13.8223 2.74187 13.3232L4.17058 9.07129L0.520188 6.44629C0.0963601 6.14551 -0.0813742 5.83105 0.0348367 5.48926C0.144212 5.16113 0.465501 4.99707 0.991868 5.00391L5.46941 5.03125L6.82976 0.758789C6.99382 0.25293 7.23991 0 7.58855 0C7.94402 0 8.19011 0.25293 8.35417 0.758789L9.71452 5.03125L14.1921 5.00391C14.7184 4.99707 15.0397 5.16113 15.1491 5.48926C15.2585 5.83105 15.0876 6.14551 14.6637 6.44629L11.0134 9.07129L12.4421 13.3232C12.613 13.8223 12.5514 14.1709 12.2712 14.3828C11.9841 14.6016 11.6354 14.5264 11.2116 14.2188L7.58855 11.5596L3.97234 14.2188C3.54851 14.5264 3.19304 14.6016 2.91277 14.3828Z" />
                        </svg>
                    </button>
                </div>

                <div className={style["wrapper--kiosk-detail"]}>
                    <h4 className={style["text--kiosk-detail"]}>키오스크 현황</h4>

                    <div className={style["unit--kiosk-detail-cell"]}>
                        <div className={style["wrapper--kiosk-detail-cell"]}>
                            <p
                                className={[
                                    style["text--kiosk-detail-cell"],
                                    style["text--kiosk-detail-cell--copy"],
                                ].join(" ")}
                            >
                                투입 가능 여부
                            </p>
                            {available ? (
                                <h4
                                    className={[
                                        style["text--kiosk-detail-cell"],
                                        style["text--kiosk-detail-cell--available"],
                                    ].join(" ")}
                                >
                                    페트 투입 가능
                                </h4>
                            ) : (
                                <h4
                                    className={[
                                        style["text--kiosk-detail-cell"],
                                        style["text--kiosk-detail-cell--unavailable"],
                                    ].join(" ")}
                                >
                                    페트 투입 불가능
                                    <span>{message}</span>
                                </h4>
                            )}
                        </div>
                    </div>

                    <h4 className={style["text--kiosk-detail"]}>키오스크 정보</h4>

                    <div className={style["unit--kiosk-detail-cell"]}>
                        <div className={style["wrapper--kiosk-detail-cell"]}>
                            <p
                                className={[
                                    style["text--kiosk-detail-cell"],
                                    style["text--kiosk-detail-cell--copy"],
                                ].join(" ")}
                            >
                                주소
                            </p>
                            <h4 className={[style["text--kiosk-detail-cell"]].join(" ")}>{address}</h4>
                        </div>
                        <hr />
                        <div className={style["wrapper--kiosk-detail-cell"]}>
                            <p
                                className={[
                                    style["text--kiosk-detail-cell"],
                                    style["text--kiosk-detail-cell--copy"],
                                ].join(" ")}
                            >
                                투입 가능 개수
                            </p>
                            <h4 className={[style["text--kiosk-detail-cell"]].join(" ")}>
                                {maxCapacity - currentCapacity}개 투입 가능 · 최대 {maxCapacity}개
                            </h4>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const Marker = ({ click, available }: { click: () => void; available: boolean }) => {
    return (
        <div onClick={click} className={[style["marker"], !available && style["marker--unavailable"]].join(" ")}>
            {available ? "페트 투입 가능" : "페트 투입 불가능"}
        </div>
    );
};

export default Location;
